<script setup lang="ts">
import type { Components } from '@ionic/core'
import type { AssociationMembershipResponse } from '~~/openapi-client/gen/schemas'
import Success from '~/pages/modals/transferBoardRole/existingMember/5.success.modal.vue'
import { getFileName } from '~/utils/blob'

const props = defineProps<{
	nav: ComponentPublicInstance<Components.IonNav>
	selectedMember: AssociationMembershipResponse
}>()

const nav = props.nav

const {
	resign,
	resignAttrs,
	functionalTagName,
	functionalTagNameAttrs,
	electionProtocol,
	registerExcerpt,
	articlesOfAssociation,
	isSubmitting,
	submit,
	validate,
} = useTransferBoardMembershipForm()

const { membership, salutation } = useUserMembership()

const next = async () => {
	const validationResult = await validate()
	if (validationResult.valid) {
		await submit(props.selectedMember.id)
		nav.push(Success, { nav })
	} else {
		throw createError({
			statusCode: 400,
			statusMessage: 'Validierungsfehler',
			data: validationResult.errors,
		})
	}
}
</script>

<template>
	<ModalContent
		title="Übersicht"
		show-back-button
	>
		<Stepper
			:step="5"
			:steps="5"
		/>
		<section>
			<NuxtErrorBoundary>
				<ion-list lines="full">
					<ion-item-divider>
						<ion-label>Ich:</ion-label>
					</ion-item-divider>
					<VCIFormSpacer />
					<VCIFormItemInput
						:value="salutation"
						label="Anrede"
						readonly
					/>
					<VCIFormItemInput
						:value="membership!.firstname"
						label="Vorname"
						readonly
					/>
					<VCIFormItemInput
						:value="membership!.lastname"
						label="Nachname"
						readonly
					/>
					<VCIFormSpacer />
					<ion-item-divider>
						<ion-label>Möchte meine Rolle:</ion-label>
					</ion-item-divider>
					<VCIFormSpacer />
					<VCIFormItemInput
						:value="RoleName[membership!.role]"
						label="Rechte im Portal"
						readonly
					/>
					<VCIFormItemInput
						:value="membership!.tags.flatMap(tag => tag.name).join(', ')"
						label="Rollen im Verein"
						readonly
					/>
					<VCIFormSpacer />
					<ion-item-divider>
						<ion-label>Übertragen an:</ion-label>
					</ion-item-divider>
					<VCIFormSpacer />
					<MemberListItem
						:member="selectedMember"
						hide-select
						data-cy="selected-member"
					/>
					<VCIFormSpacer />
					<ion-item-divider>
						<ion-label id="resignLabel">
							Ich bleibe nach Übertragung der Rolle weiterhin Vorstandsmitglied:
						</ion-label>
					</ion-item-divider>
					<VCIFormSpacer />
					<VCIFormItemInput
						:value="resign? 'Nein' : 'Ja'"
						v-bind="resignAttrs"
						label=" "
						aria-labelledby="resignLabel"
						readonly
						data-cy="resign-decision"
					/>
					<VCIFormItemInput
						:value="functionalTagName"
						v-bind="functionalTagNameAttrs"
						label="Rechte im Portal"
						readonly
					/>
					<VCIFormSpacer />
					<ion-item-divider>
						<ion-label>Sie haben folgende Dokumente ausgewählt:</ion-label>
					</ion-item-divider>
					<VCIFormSpacer />
					<VCIFormItemInput
						:value="getFileName(electionProtocol)"
						v-bind="functionalTagNameAttrs"
						label="Wahlprotokoll"
						readonly
					/>
					<VCIFormItemInput
						:value="getFileName(articlesOfAssociation)"
						v-bind="functionalTagNameAttrs"
						label="Vereinssatzung"
						readonly
					/>
					<VCIFormItemInput
						:value="getFileName(registerExcerpt)"
						v-bind="functionalTagNameAttrs"
						label="Vereinsregister"
						readonly
					/>
					<VCIFormSpacer />
				</ion-list>
				<template #error="{ error, clearError }">
					<p>An error occurred: {{ error }}</p>

					<VCIButton @click="clearError">
						Clear error
					</VCIButton>
				</template>
			</NuxtErrorBoundary>
		</section>

		<template #actions>
			<VCIButton
				data-cy="transfer-board-role-submit"
				icon="mdi-arrow-right"
				@click="next"
			>
				Rolle übertragen
			</VCIButton>
		</template>
		<VCILoading
			message="Bitte warten..."
			:is-open="isSubmitting"
		/>
	</ModalContent>
</template>

<style lang="scss" scoped>
ion-item-divider {
	--background: var(--ion-color-grey-5);
	--inner-padding-top: 16px;
	--inner-padding-start: 24px;
	--inner-padding-bottom: 16px;
	--padding-start: 0;
	font-size: 15px;
}
</style>
